import {
    AsyncPipe,
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    ViewportScroller
} from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    forwardRef,
    inject,
    Input,
    OnChanges,
    QueryList,
    SimpleChanges
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import _ from "lodash";
import { Subject } from "rxjs";
import { ListColumnDirective } from "~app/common/components/list/list-column.directive";
import { ListSorting, ListState } from "~app/common/components/list/list.models";
import { StatedataService, Volatile } from "~app/common/services/statedata.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationMessageRowComponent } from "../../../validation/validation-message-row/validation-message-row.component";
import { ListCustomRowDirective } from "../list-custom-row.directive";
import { RowDragDirective } from "./row-drag.directive";

const compare = (a: number | string, b: number | string, isAsc: boolean) => (a < b ? -1 : 1) * (isAsc ? 1 : -1);

@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DivListComponent)
        }
    ],
    selector: "rask-div-list",
    styleUrls: ["./div-list.component.scss"],
    templateUrl: "./div-list.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RowDragDirective,
        NgTemplateOutlet,
        NgClass,
        NgStyle,
        ValidationMessageRowComponent,
        AsyncPipe
    ]
})
export class DivListComponent implements OnChanges, AfterViewInit {
    readonly availableWidthReady$: Subject<boolean>;

    columnWidths: string[] = [];
    @ContentChildren(ListColumnDirective) columns: QueryList<ListColumnDirective>;
    @ContentChildren(ListCustomRowDirective) customRows: QueryList<ListCustomRowDirective>;
    @Input() data: any[];
    dataSorted: any[];
    @Input() fixedHeader = false;
    getSelected = true;
    @Input() getSelectedFromRoute = false;
    @Input() innerHeader = false;
    @Input() key: string;
    listState: ListState;
    @Input() openFn = null;
    @Input() rowDrag = false;
    @Input() selected: string;
    @Input() showHeaderWhenEmpty = true;
    @Input() sortOrder: string;
    sorting = false;
    @Input() supressHeader = false;
    volatile: Volatile;

    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly elRef = inject(ElementRef);
    private readonly statedataService = inject(StatedataService);
    private readonly viewScroller = inject(ViewportScroller);

    constructor() {
        this.volatile = this.statedataService.getVolatile();
        this.listState = new ListState();
        this.availableWidthReady$ = new Subject<boolean>();
    }

    generateId(index: number): string {
        if (this.key) {
            const keyValue = this.dataSorted[index][this.key];
            return this.generateRowId(keyValue);
        }

        return this.dataSorted[index].id ? this.generateRowId(this.dataSorted[index].id) : this.generateRowId(index);
    }

    generateRowId(val: any): string {
        return  `row_${val}`;
    }

    getSortedData(): any[] {
        return this.dataSorted;
    }

    hasValidationMessages(row: any): boolean {
        return row?.validationMessages?.length > 0;
    }

    isSelected(index: number, last: boolean): boolean {
        const rowId = this.generateId(index);
        if (last && this.selected) {
            this.scrollTo(this.generateRowId(this.selected));
        }

        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (rowId === this.generateRowId(this.selected)) {
            return true;
        }

        return false;
    }

    isSortActive(column: ListColumnDirective): boolean {
        return this.listState.sorting && this.listState.sorting.field === column.field && column.sortable;
    }

    isSortReverse(column: ListColumnDirective): boolean | null {
        return this.listState.sorting.field === column.field ? this.listState.sorting.reverse : null;
    }

    ngAfterViewInit(): void {
        const el = this.elRef.nativeElement.querySelector("div");

        const resize = () => {
            if(!el || !parseInt(window.getComputedStyle(el).width, 10)) {
                return;
            }

            const availableWidth = parseInt(window.getComputedStyle(el).width, 10);

            let remaining = availableWidth;
            this.columns.filter(x => ObjectUtils.stringHasValue(x.width)).forEach(x => {
                if (x.width.indexOf("%") > -1) {
                    const num = x.width.replace("%", "");
                    remaining -= availableWidth * (+num / 100);
                } else {
                    const num = x.width.replace("px", "");
                    remaining -= +num;
                }
            });

            const len = this.columns.filter(x => !ObjectUtils.stringHasValue(x.width)).length;
            const defaultWidth = `${len === 0 ? len : remaining / len}px`;

            this.columnWidths = this.columns.map(x => ObjectUtils.stringHasValue(x.width) ? x.width : defaultWidth);
            this.availableWidthReady$.next(true);
            this.cdRef.detectChanges();
        };

        const debounced = _.debounce(resize, 100);
        const resizeObserver = new ResizeObserver(() => debounced());
        resizeObserver.observe(el);
    }

    ngOnChanges(changes: SimpleChanges) {
        // Reset sorting when changing datasource
        if (changes.data && this.data) {
            this.dataSorted = this.data.slice();
            this.listState = this.volatile.get<ListState>("sorting");
            if (this.listState && this.listState.sorting) {
                this.dataSorted = this.sortBy(this.listState.sorting.field, !this.listState.sorting.reverse);
            } else {
                this.listState = new ListState();
            }

            if (this.getSelectedFromRoute) {
                if (this.selected) {
                    this.selected = null;
                } else if (this.getSelected) {
                    this.selected = this.getSelectedFromRouteParam();
                    this.getSelected = false;
                }
            }
        }
    }

    setSorting(column: ListColumnDirective): void {
        this.selected = null;
        if (!column.sortable) {
            return;
        }

        this.sorting = true;
        if (this.listState.sorting.field !== column.field) {
            this.listState.sorting.reverse = null;
        }

        this.listState.sorting.field = column.field;
        this.listState.sorting.reverse = this.setSortReverse(this.listState.sorting.reverse);
        if (this.listState.sorting.reverse === null) {
            this.dataSorted = this.data.slice();
        } else {
            this.dataSorted = this.sortBy(this.listState.sorting.field, !this.listState.sorting.reverse);
        }
        this.sorting = false;
        this.volatile.set("sorting", this.listState);
    }

    trackById = (index: number, row: any): number => {
        if (this.key) {
            return row[this.key];
        }

        return row.id ? row.id : index;
    };

    private clear(): void {
        this.clearSelected();
        this.clearSorting();
    }

    private clearSelected(): void {
        this.selected = null;
    }

    private clearSorting(): void {
        this.listState.sorting = new ListSorting("", null);
    }

    private getSelectedFromRouteParam(): string {
        if (this.key) {
            return this.statedataService.previousParams ? this.statedataService.previousParams[this.key] : null;
        }

        return this.statedataService.previousParams ? this.statedataService.previousParams.id : null;
    }

    private scrollTo(tag: string): void {
        this.viewScroller.scrollToAnchor(tag);
    }

    private setSortReverse(reverse: boolean): boolean {
        if (reverse) {
            reverse = null;
        } else if (ObjectUtils.isNullOrUndefined(reverse)) {
            reverse = false;
        } else {
            reverse = !reverse;
        }

        return reverse;
    }

    private sortBy(prop: string, isAsc: boolean): any[] {
        const sortedData = this.data.slice();
        if (!prop) {
            return sortedData;
        }

        return sortedData.sort((a, b) => compare(a[prop], b[prop], isAsc));
    }
}
